// ExampleResearch.js

import React, { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import greenProductsData from '../components/exampleresearches/test1.json';
import aiConsumerData from '../components/exampleresearches/test2.json';
import onlineShoppingData from '../components/exampleresearches/test3.json';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonaBarChart from '../components/PersonaBarChart';
import CircularProgress from '@mui/material/CircularProgress';
import BarChartComponent from '../components/BarChartComponent';
import { ThemeProvider } from '@mui/material/styles';
import ResearchTheme from '../styles/Theme';
import { useAuth } from '../authentication/AuthContext';



import {
  Box,
  Paper,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemText,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ListItemIcon,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';

function ExampleResearch() {
  
  // Extract filter and example from location state if present
  
  const [topics, setTopics] = useState([]);
  const [selectedAttributePersonas, setSelectedAttributePersonas] = useState('age_range');
  const [groupByPersonas, setGroupByPersonas] = useState('');
  const [groupByResponses, setGroupByResponses] = useState({});
  const [filters, setFilters] = useState({});
  const [analysisResult, setAnalysisResult] = useState(null);
  const [visibleSections, setVisibleSections] = useState({});
  const [loading, setLoading] = useState(false);
  const [responsesAnalysis, setResponsesAnalysis] = useState({});
  const [dataLoaded, setDataLoaded] = useState(false);
  const { isAdmin } = useAuth();
  const { researchId } = useParams();
  const location = useLocation();
  const path = location.pathname.split('/').pop();

  const getResearchData = () => {
    if (path === 'Green%20Products%20Market%20Report' || 
        path === 'Green Products Market Report') {
      return greenProductsData;
    } else if (path === 'AI%20Consumer%20Trends%20Study' || 
          path === 'AI Consumer Trends Study') {
        return aiConsumerData;
      } 
      else if (path === 'Online%20Shopping%20Experience' || 
          path === 'Online Shopping Experience') {
        return onlineShoppingData;
      }
    return require('../components/exampleresearches/test0.json');
  }; 


  const decodedPath = decodeURIComponent(path);
  console.log("Decoded path:", decodedPath); // Add this for debugging

  
  const exampleData = getResearchData();


  function groupResponsesByAttribute(responses, personasObj, attribute) {
    if (!responses || responses.length === 0 || !personasObj || !attribute) {
      return [];
    }
    
    const result = {};
    const allGroups = new Set();

    responses.forEach(({ response, personaID }) => {
      const persona = personasObj[personaID] || {};
      const attrValue = persona[attribute] || 'Unknown';
      const answers = Array.isArray(response) ? response : [response];

      allGroups.add(attrValue);

      answers.forEach((option) => {
        if (!result[option]) {
          result[option] = { option };
        }
        result[option][attrValue] = (result[option][attrValue] || 0) + 1;
      });
    });

    // fill in zeros for missing groups
    const groupsArray = Array.from(allGroups);
    Object.values(result).forEach((item) => {
      groupsArray.forEach((g) => {
        if (!(g in item)) item[g] = 0;
      });
    });

    return Object.values(result);
  }

  useEffect(() => {
    try {
      console.log("Loading example data for:", path);
      console.log("Loading example data:", exampleData);
      
      // Process the example data from test1.json
      const { researchID, description, title, questions, personas, responses } = exampleData;
      
      // Transform questions to match expected format
      const surveyQuestions = {};
      questions.forEach(q => {
        surveyQuestions[q.questionID] = {
          prompt: q.questionText,
          kind: q.questionType,
          vocab: q.responseOptions || [],
          min: q.selectMin || "1",
          max: q.selectMax || "1"
        };
      });
      
      // Create topic object
      const topic = {
        researchID,
        description,
        title,
        survey: {
          questions: surveyQuestions
        },
        selectedPersonas: personas,
        surveyResponses: responses,
        personaFilters: []
      };
      
      console.log("Processed topic:", topic);
      setTopics([topic]);
      setFilters({ numberOfPersonas: Object.keys(personas).length });
      setDataLoaded(true);
    } catch (error) {
      // console.error("Error loading example data:", error);
    }
  }, []);

  if (!dataLoaded || topics.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>Loading example research data...</Typography>
      </Box>
    );
  }

  const firstTopic = topics[0] || {};
  const surveyObj = firstTopic.survey || {};
  const questionEntries = Object.entries(surveyObj.questions || {});
  const researchTitle = firstTopic?.title ?? 'Example Research Title';
  const researchDescription = firstTopic?.description ?? 'Research Question: (No data found)';
  const personas = firstTopic?.selectedPersonas || {};
  const responses = firstTopic.surveyResponses || [];

  const ageOrder = ["18-24", "25-34", "35-44", "45-54", "55-64", "65+"];
  const incomeOrder = [
    "<10000", "10000-19999", "20000-29999", "30000-39999",
    "40000-49999", "50000-99999", "100000-199999", ">200000"
  ];
  const educationOrder = [
    "Elementary", "Low High school", "MBO/ High school",
    "HBO/WO Bachelor", "Master or PHD"
  ];

  const sortByOrder = (data, order) => {
    return [...data].sort((a, b) => order.indexOf(a.name) - order.indexOf(b.name));
  };  

  const prepareStackedData = (attribute, groupByPersonas) => {
    if (!attribute || !personas || Object.keys(personas).length === 0) return [];
    const groupedData = {};
    const totalCount = Object.keys(personas).length;
    
    Object.entries(personas).forEach(([pID, persona]) => {
      const attrValue = persona[attribute];
      const groupValue = persona[groupByPersonas] || 'value';
      
      if (!groupedData[attrValue]) {
        groupedData[attrValue] = { name: attrValue, count: 0 }; 
      }
      groupedData[attrValue][groupValue] = (groupedData[attrValue][groupValue] || 0) + 1;
      groupedData[attrValue].count += 1;
    });

    let result = Object.values(groupedData).map(entry => {
      entry.value = parseFloat(((entry.count / totalCount) * 100).toFixed(1));
      return entry;
    });

    if (attribute === "age_range") {
      result = sortByOrder(result, ageOrder);
    } else if (attribute === "income") {
      result = sortByOrder(result, incomeOrder);
    } else if (attribute === "education") {
      result = sortByOrder(result, educationOrder);
    }

    if (groupByPersonas) {
      result = result.map(({ count, value, ...rest }) => rest);
    }
    
    return result;
  };

  const formatAttributeLabel = (attr) =>
    attr.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());


  const handleGenerateAnalysis = async () => {
    try {
      setLoading(true);
      
      // Mock analysis result since we're not using real API
      const mockAnalysisResult = {
        content: {
          sections: [
            {
              heading: "Key Findings",
              paragraphs: [
                "AI tools significantly enhance the shopping experience by providing personalized recommendations and streamlining the decision-making process.",
                "Most respondents across all age groups report that AI tools make purchasing decisions easier."
              ],
              bullets: [],
              subsections: [
                {
                  subheading: "Demographics Impact",
                  paragraphs: [
                    "Younger and more educated users tend to be more comfortable with AI shopping tools.",
                    "Both men and women find AI recommendations useful, with slight preference variations by product category."
                  ],
                  bullets: []
                }
              ]
            },
            {
              heading: "Concerns and Limitations",
              paragraphs: [
                "Privacy concerns are prevalent across all demographic groups.",
                "Some users worry about over-reliance on AI recommendations."
              ],
              bullets: [],
              subsections: []
            }
          ]
        }
      };

      setAnalysisResult(mockAnalysisResult);

      setVisibleSections({});

      mockAnalysisResult?.content?.sections.forEach((_, index) => {
        setTimeout(() => {
          setVisibleSections(prev => ({ ...prev, [index]: true }));
        }, index * 500);
      });
      
    } catch (error) {
      // console.error('Error generating analysis:', error);
    } finally {
      setLoading(false); 
    }
  };


  const handleGroupByChange = (questionID, newValue) => {
    setGroupByResponses(prevState => ({
      ...prevState,
      [questionID]: newValue
    }));
  };

 
  const handleGenerateResponseAnalysis = async (questionID) => {
    try {
      setLoading(true);

      // Get relevant responses for the question
      const relevantResponses = responses.filter((r) => r.questionID === questionID);

      // Mock analysis result
      const mockResponseAnalysis = {
        analysis_text: `Based on the data, most respondents across all demographics prefer using AI tools for shopping. 
        The ${relevantResponses.length} responses show strong preference patterns with slight variations by age and gender.
        Privacy concerns are consistent across all groups but don't deter usage.`
      };

      setResponsesAnalysis(prev => ({
        ...prev,
        [questionID]: mockResponseAnalysis
      }));

    } catch (error) {
      // console.error('Error generating analysis:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ThemeProvider theme={ResearchTheme}>
      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', mb: 4 }}>
        
        {/* 1. Research Question Section */}
        <Paper
          sx={{
            position: 'relative',
            p: 4,
            mb: 3,
            borderRadius: 2,
            height: { xs: '250px', md: '350px' },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'var(--secondary-color)',
            backgroundImage: `linear-gradient(
              rgba(0,0,0,0.55), 
              rgba(0,0,0,0.55)
            ), url("/Example-1.png")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            color: 'var(--primary-color)',
            textAlign: 'center',
          }}
          elevation={4}
        >
          <Box 
            sx={{
              position: 'absolute',
              inset: 0,
              backgroundColor: 'rgba(0,0,0,0.2)' 
            }}
          />
          <Box sx={{ position: 'relative', zIndex: 1, maxWidth: '700px' }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 2 }}>
              {researchTitle}
            </Typography>
            <Typography variant="h6" sx={{ fontStyle: 'italic' }}>
              {researchDescription}
            </Typography>
          </Box>
        </Paper>

        {/* ========================================================================= */}
        {/* ======================= SECTION DIVIDER ================================= */}
        {/* ========================================================================= */}


        {/* 2. Survey and Personas Section - Two Column Layout */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, 
          gap: 3,
          mb: 4,
          mx: 4
        }}>
          {/* Survey Section */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Questions Section */}
            <Paper 
              sx={{ 
                p: 3, 
                borderRadius: 2,
                backgroundColor: 'var(--secondary-color)',
                boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                maxHeight: '450px',
                overflow: 'auto'
              }}
            >
              <Box sx={{ position: 'relative', mb: 2 }}>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    color: 'var(--text-color)',
                    fontWeight: 600,
                    zIndex: 2,
                    position: 'relative',
                    transform: 'translateY(-20%)'
                  }}
                >
                  Survey Questions
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontStyle: 'italic', 
                    color: 'text.secondary',
                    fontSize: '0.875rem',
                    position: 'absolute',
                    right: 0,
                    top: '50%',
                    transform: 'translateY(+60%)',
                    zIndex: 1
                  }}
                >
                  View answer options
                </Typography>
              </Box>

              <Divider sx={{ mb: 3, backgroundColor: 'var(--primary-color)'}} />

              {!questionEntries.length && (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  No survey questions available
                </Typography>
              )}

              {questionEntries.map(([questionID, qData], index) => {
                const questionText = qData.prompt || '(No prompt)';
                const questionType = qData.kind || 'unknown';
                const vocab = qData.vocab || [];

                return (
                  <Accordion
                    key={questionID}
                    disableGutters
                    sx={{
                      mb: 2,
                      borderRadius: '8px',
                      border: `1px solid var(--secondary-color)`,
                      boxShadow: '0 3px 5px rgba(217, 217, 217, 30)',
                      overflow: 'hidden',
                      '&:before': { display: 'none' }, 
                    }}
                  >
                    <AccordionSummary
                      expandIcon={
                        <ExpandMoreIcon sx={{ color: 'var(--darker-color)' }} />
                      }
                      sx={{
                        color: 'var(--light-text)',
                        backgroundColor: 'var(--primary-color)',
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                        {`Q${index + 1}: ${questionText}`}
                      </Typography>
                    </AccordionSummary>

                    <AccordionDetails sx={{ 
                      backgroundColor: '#fefefe', 
                      borderTop: '1px solid var(--light-grey)' 
                    }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
                        {`Type: ${questionType}`}
                      </Typography>

                      {(questionType === 'multiple-choice') && (
                        <Box 
                          sx={{ 
                            border: '1px solid var(--light-grey)', 
                            borderRadius: 1, 
                            p: 2, 
                            mb: 2, 
                            backgroundColor: '#fff' 
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                            Possible Answers:
                          </Typography>
                          <List disablePadding>
                            {vocab.map((option, idx) => (
                              <ListItem key={idx} sx={{ py: 0 }}>
                                <ListItemIcon sx={{ minWidth: '36px' }}>
                                  • 
                                </ListItemIcon>
                                <ListItemText primary={option} />
                              </ListItem>
                            ))}
                          </List>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                            {`Select between ${qData.min} and ${qData.max} option(s).`}
                          </Typography>
                        </Box>
                      )}

                      {/* If open-ended */}
                      {questionType === 'open-ended' && (
                        <Typography variant="body2" sx={{ fontWeight: "bold", mt: 0 }}>
                          This question is open-ended (no fixed choices).
                        </Typography>
                      )}

                      {questionType === 'open-list' && (
                        <Typography variant="body2" sx={{ fontWeight: "bold" ,mb: 0 }}>
                          This question is open-list (no fixed choices).
                        </Typography>
                      )}
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Paper>

            {/* Analysis Section */}
            <Paper 
              sx={{ 
                p: 3, 
                borderRadius: 2,
                backgroundColor: 'var(--secondary-color)',
                boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                maxHeight: '450px',
                overflow: 'auto'
              }}
            >
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
                Analysis
              </Typography>
              <Divider sx={{ mt: 1, mb: 3, borderColor: 'var(--tertiary-color)', borderWidth: 2 }} />
              
              {analysisResult && analysisResult.content && (
                <Box sx={{ mt: 2 }}>
                  {analysisResult.content.sections.map((section, i) => (
                    <Box 
                      key={i} 
                      sx={{ 
                        pr: 3,
                        pl: 3,
                        pt: 3,
                        opacity: visibleSections[i] ? 1 : 0,
                        transform: visibleSections[i] ? 'translateY(0)' : 'translateY(10px)',
                        transition: 'opacity 0.5s ease, transform 0.5s ease',
                      }}
                    >
                      <Typography variant="h6" sx={{ 
                        fontWeight: 600, 
                        mb: 3, 
                        backgroundColor: 'var(--cadet-blue)', 
                        borderRadius: '8px',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
                        position: 'relative',
                        overflow: 'hidden',
                        padding: '8px 16px',
                        width: 'fit-content',
                        color: 'var(--primary-color)'
                      }}>
                        {section.heading}
                      </Typography>

                      {section.paragraphs.map((para, pIdx) => (
                        <Typography variant='h6' key={pIdx} sx={{ mb: 2, pl: 2, fontWeight: 500, lineHeight: 1.6 }}>
                          {para}
                        </Typography>
                      ))}

                      {section.bullets.length > 0 && (
                        <ul style={{ marginLeft: '1rem' }}>
                          {section.bullets.map((bullet, bIdx) => (
                            <li key={bIdx}>{bullet}</li>
                          ))}
                        </ul>
                      )}

                      {section.subsections.map((sub, sIdx) => (
                        <Box key={sIdx} sx={{ 
                          fontWeight: 500, 
                          mb: 2,
                          mr: 1,
                          ml: 1, 
                          backgroundColor: 'var(--primary-color)', 
                          borderRadius: '8px',
                          boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
                          position: 'relative',
                          overflow: 'hidden',
                          padding: '8px 16px',
                        }}>
                          <Typography variant="h6" sx={{ fontWeight: 'bold', pl: 1, pr: 1, pb: 2, pt: 2}}>
                            {sub.subheading}
                          </Typography>

                          <Divider sx={{ mb: 2, ml:1, mr:1, backgroundColor: 'var(--tertiary-color)', borderWidth: 1.2 }} />

                          {sub.paragraphs.map((subPara, spIdx) => (
                            <Typography variant='body1' key={spIdx} sx={{ lineHeight: 1.6, mb: 1, pl: 2, fontSize: '1.1rem' }}>
                              {subPara}
                            </Typography>
                          ))}

                          {sub.bullets.length > 0 && (
                            <ul style={{ marginLeft: '1rem' }}>
                              {sub.bullets.map((bullet, sbIdx) => (
                                <li style={{ fontSize: '1.1rem', fontWeight: '400' }} key={sbIdx}>{bullet}</li>
                              ))}
                            </ul>
                          )}
                        </Box>
                      ))}
                    </Box>
                  ))}
                </Box>
              )}

              <Box sx={{ textAlign: 'center', mt: 3, mb: 3 }}>
                {loading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                    <CircularProgress />
                  </Box>
                )}

                {isAdmin && (
                  <Button
                    variant="contained"
                    onClick={() => {
                      setVisibleSections({});
                      handleGenerateAnalysis();
                    }}
                    sx={{ boxShadow: 'none', fontWeight: 'bold', backgroundColor: 'var(--tertiary-color)', mt: 4, p: 2 }}
                    disabled={loading}
                  >
                    {loading ? 'Loading...' : analysisResult ? 'Regenerate Analysis' : 'Generate Analysis'}
                  </Button>
                )}
              </Box>
            </Paper>
          </Box>

          {/* ========================================================================= */}
          {/* ======================= SECTION DIVIDER ================================= */}
          {/* ========================================================================= */}
          
          {/* Personas Section */}
          <Paper sx={{ 
            p: 3, 
            borderRadius: 2, 
            backgroundColor: 'var(--secondary-color)', 
            boxShadow: '0 1px 4px rgba(0,0,0,0.15)', 
            height: '972px', 
            
          }}>
            <Typography variant="h5" sx={{ 
              mb: 2, 
              color: 'var(--text-color)',
              fontWeight: 600
            }}>Personas</Typography>
            <Divider sx={{ my: 2, backgroundColor:'var(--primary-color)' }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, }}>
              <Box sx={{ backgroundColor: '#fff', borderRadius: 2, p: 2, minWidth: '170px', boxShadow: '0 3px 5px rgba(217, 217, 217, 30)', }}>
                <ToggleButtonGroup
                  orientation="horizontal"
                  value={selectedAttributePersonas}
                  exclusive
                  onChange={(e, newAttr) => {
                    // Only update if user clicked a valid button
                    if (newAttr) setSelectedAttributePersonas(newAttr);
                  }}
                  size="small"
                >
                  <ToggleButton value="gender">Gender</ToggleButton>
                  <ToggleButton value="age_range">Age</ToggleButton>
                  <ToggleButton value="employment_status">Employment</ToggleButton>
                  <ToggleButton value="income">Income</ToggleButton>
                  <ToggleButton value="education">Education</ToggleButton>
                  <ToggleButton value="household_situation">Household</ToggleButton>
                  <ToggleButton value="marital_status">Marital</ToggleButton>
                </ToggleButtonGroup>

                <FormControl size="small" sx={{ mt: 2, width: '100%' }}>
                  <InputLabel>Show other data</InputLabel>
                  <Select
                    label="Show other data"
                    value={groupByPersonas}
                    onChange={(e) => setGroupByPersonas(e.target.value)}
                  >
                    <MenuItem value="">None</MenuItem>
                    <MenuItem value="gender">Gender</MenuItem>
                    <MenuItem value="age_range">Age Range</MenuItem>
                    <MenuItem value="employment_status">Employment Status</MenuItem>
                    <MenuItem value="income">Income</MenuItem>
                    <MenuItem value="education">Education</MenuItem>
                    <MenuItem value="household_situation">Household Situation</MenuItem>
                    <MenuItem value="marital_status">Marital Status</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ backgroundColor: '#fff', borderRadius: 2, p: 2, boxShadow: '0 3px 5px rgba(217, 217, 217, 30)' }}>
                <PersonaBarChart
                  data={prepareStackedData(selectedAttributePersonas, groupByPersonas)}
                  selectedAttribute={selectedAttributePersonas}
                  groupBy={groupByPersonas}
                  prepareStackedData={prepareStackedData}
                  formatAttributeLabel={formatAttributeLabel}
                  height={400}
                />
              </Box>

              <Box sx={{ flex: 1, backgroundColor: '#fff', borderRadius: 2, p: 2, boxShadow: '0 3px 5px rgba(217, 217, 217, 30)',}}>
                <Typography variant="h6" sx={{ mb: 2 }}>Active Filters</Typography>
                
                {/* Display number of personas at the top */}
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Number of Personas: {Object.keys(personas).length}
                </Typography>
                
                <Accordion 
                  disableGutters
                  sx={{
                    mb: 2,
                    borderRadius: '8px',
                    border: `1px solid var(--light-grey)`,
                    boxShadow: 'none',
                    overflow: 'hidden',
                    '&:before': { display: 'none' }, 
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <ExpandMoreIcon sx={{ color: 'var(--darker-color)' }} />
                    }
                    sx={{
                      backgroundColor: 'var(--primary-color)',
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                      View Active Filters
                    </Typography>
                  </AccordionSummary>
                  
                  <AccordionDetails sx={{ 
                    backgroundColor: '#fefefe', 
                    borderTop: '1px solid var(--light-grey)' 
                  }}>
                    {[
                      "gender",
                      "age_range",
                      "employment_status",
                      "income",
                      "education",
                      "marital_status",
                      "household_situation"
                    ].map(attribute => (
                      <Box key={attribute} sx={{ mb: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                          {attribute.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase())}:
                        </Typography>
                        <Typography variant="body2" sx={{ ml: 2 }}>
                          {Array.isArray(filters[attribute]) && filters[attribute]?.length > 0 
                            ? filters[attribute].join(', ') 
                            : 'None'}
                        </Typography>
                      </Box>
                    ))}
                  </AccordionDetails>
                </Accordion>
              </Box>
            </Box>
          </Paper>
        </Box>

        {/* ========================================================================= */}
        {/* ======================= SECTION DIVIDER ================================= */}
        {/* ========================================================================= */}
        
        {/* 4. Responses & Analysis Section */}
        <Paper sx={{ p: 4, mb: 2, mr: 4, ml: 4, backgroundColor: 'var(--secondary-color)'}}>
          <Typography variant="h5" sx={{ 
              mb: 2, 
              fontWeight: 600
            }}>Data Analysis</Typography>
          <Divider sx={{ my: 2, backgroundColor: 'var(--primary-color)', borderWidth: 1 }} />

          {questionEntries.map(([questionID, questionData], index) => {
            if (questionData.kind !== "multiple-choice") {
                return null;
            }

            const questionGroupBy = groupByResponses[questionID] || 'gender';

            const relevantResponses = responses.filter(
              (r) => r.questionID === questionID
            );

            const responseCounts = [];
            relevantResponses.forEach((resp) => {
              if (Array.isArray(resp.response)) {
                resp.response.forEach((ans) => {
                  const found = responseCounts.find((rc) => rc.option === ans);
                  if (found) {
                    found.count += 1;
                  } else {
                    responseCounts.push({ option: ans, count: 1 });
                  }
                });
              } else if (resp.response) {
                const ans = resp.response;
                const found = responseCounts.find((rc) => rc.option === ans);
                if (found) {
                  found.count += 1;
                } else {
                  responseCounts.push({ option: ans, count: 1 });
                }
              }
            });

            const groupedData = groupResponsesByAttribute(
              relevantResponses,
              personas,
              questionGroupBy
            );

            return (
              <Accordion 
                key={questionID} 
                sx={{ 
                  mb: 2, 
                  borderRadius: 2, 
                  overflow: 'hidden',
                  '&:before': { display: 'none' },
                  boxShadow: '0 1px 4px rgba(0,0,0,0.15)'
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: 'var(--light-text)' }} />}
                  sx={{
                    backgroundColor: 'var(--background-color)',
                    borderRadius: '8px 8px 0 0',
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 500, color: 'var(--light-text)' }}>
                    {`Question ${index + 1}: ${questionData.prompt}`}
                  </Typography>
                </AccordionSummary>
                
                <AccordionDetails sx={{ 
                  p: 3, 
                  backgroundColor: 'var(--background-color)',
                  
                }}>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
                    {/* Grouped Bar Chart */}
                    <Box sx={{ flex: 1, p: 6, backgroundColor: '#fff', borderRadius: 2 }}>
                      <BarChartComponent
                        questionText={questionData.prompt}
                        groupedResponses={groupedData}
                        attribute={questionGroupBy}
                      />
                      <Box sx={{ mt: 2 }}>
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <InputLabel>Group by</InputLabel>
                          <Select
                            label="Group by"
                            value={questionGroupBy}
                            onChange={(e) => handleGroupByChange(questionID, e.target.value)}
                          >
                            <MenuItem value="gender">Gender</MenuItem>
                            <MenuItem value="age_range">Age Range</MenuItem>
                            <MenuItem value="employment_status">Employment Status</MenuItem>
                            <MenuItem value="income">Income</MenuItem>
                            <MenuItem value="education">Education</MenuItem>
                            <MenuItem value="household_situation">Household Situation</MenuItem>
                            <MenuItem value="marital_status">Marital Status</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>
                    </Box>

                    {/* Short Analysis */}
                    <Box 
                      sx={{ 
                        p: 2, 
                        backgroundColor: 'var(--secondary-color)', 
                        borderRadius: 2, 
                        width: '30%', 
                        display: 'flex', 
                        flexDirection: 'column', 
                        justifyContent: 'space-between',
                        height: '100%' 
                      }}
                    >
                      <Box>
                        <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>Short Analysis</Typography>
                        {responsesAnalysis[questionID]?.analysis_text && (
                          <Typography variant="body1">
                            {responsesAnalysis[questionID].analysis_text}
                          </Typography>
                        )}
                      </Box>
                      
                      <Box sx={{ textAlign: 'center', mt: 3 }}>
                        {isAdmin && (
                          <Button 
                            variant="contained"
                            sx={{ mr: 1, backgroundColor: 'var(--tertiary-color)'}}
                            onClick={() => handleGenerateResponseAnalysis(questionID)}
                          >
                            Regenerate
                          </Button>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Paper>
      </Box>
    </ThemeProvider>
  );
}

export default ExampleResearch;